import React from 'react';

import { Box, Button, TypographyProps } from '@mui/material';

type Props = {
  lineCount: number;
  fontSize?: number;
  color?: string;
  editColor?: string;
};

const ExpandableTypography: React.FC<Props & TypographyProps> = (props) => {
  const { children, lineCount } = props;
  const divRef = React.useRef<HTMLElement>();
  const [overflowActive, setOverflowActive] = React.useState(false);
  const [showMore, setShowMore] = React.useState(false);

  const checkOverflow = (textContainer: HTMLElement | undefined) => {
    if (textContainer) {
      return (
        textContainer.offsetHeight < textContainer.scrollHeight ||
        textContainer.offsetWidth < textContainer.scrollWidth
      );
    }
    return false;
  };

  const handleResize = () => {
    checkOverflow(divRef.current)
      ? setOverflowActive(true)
      : setOverflowActive(false);
  };

  React.useEffect(() => {
    window.addEventListener('resize', handleResize);
    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  React.useEffect(() => {
    handleResize();
  }, [children]);

  return (
    <Box
      sx={{
        width: '100%',
      }}
    >
      <Box
        ref={divRef}
        sx={{
          textOverflow: 'ellipsis',
          display: '-webkit-box',
          WebkitLineClamp: showMore ? 'none' : `${lineCount}`,
          WebkitBoxOrient: 'vertical',
          width: '100%',
          overflow: 'hidden',
          flexDirection: 'row',
          textAlign: 'left',
          pr: 0,
        }}
      >
        <pre
          style={{
            whiteSpace: 'pre-wrap',
            wordWrap: 'break-word',
            flexGrow: 1,
            position: 'relative',
            fontFamily: 'SFProDisplay-Medium',
            color: props.color ?? 'rgba(0, 33, 69, 0.70)',
            fontSize: props.fontSize ?? 18,
            fontWeight: 400,
            lineHeight: 1.4,
            marginTop: 4,
            marginBottom: 4,
            marginRight: 0,
            marginLeft: 0,
            height: '100%',
          }}
          className='small-font-on-mobile'
        >
          {children}
        </pre>
      </Box>
      {overflowActive && (
        <div
          style={{
            width: '100%',
            display: 'flex',
            justifyContent: 'flex-start',
            paddingTop: '4px',
          }}
        >
          <Button
            onClick={() => setShowMore(!showMore)}
            variant='text'
            className='show-more-button'
            sx={{
              color: props.editColor ? props.editColor : '#0CBDD4',
            }}
          >
            {showMore ? 'Show less' : 'Show more'}
          </Button>
        </div>
      )}
    </Box>
  );
};

export default ExpandableTypography;
